
import {
  computed, defineComponent, onBeforeMount, reactive,
} from 'vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import Icone from '@/core/components/Icone.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import SelecionarUnidade from '@/components/Cadastros/Produtos/Unidades/SelecionarUnidade.vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import { IProdutoCodigo, IProdutoUnidade } from '@/models/Entidades/Cadastros/Produtos/IProduto';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';

export default defineComponent({
  name: 'ProdutoUnidades',
  props: {
    empresas: {
      type: Array as () => number[],
      required: true,
    },
    codigoUnidadePrincipal: {
      type: Number,
      required: true,
    },
    fatorUnidadePrincipal: {
      type: Number,
      default: 1,
    },
    unidades: {
      type: Array as () => IProdutoUnidade[],
      required: true,
    },
    codigos: {
      type: Array as () => IProdutoCodigo[],
      required: true,
    },
  },
  components: {
    Icone,
    MensagemSemDados,
    SelecionarUnidade,
    CampoNumerico,
  },
  emits: ['update:fatorUnidadePrincipal', 'update:unidades'],
  setup(props, { emit }) {
    const { telaBase, apresentarMensagemAlerta } = useTelaBase();

    const state = reactive({
      colunas: [] as IColumn[],
      windowHeight: (window.innerHeight - 300),
      windowWidth: (window.innerWidth - 100),
      unidadeSelecionada: {} as IProdutoUnidade,
    });

    const computedUnidades = computed({
      get: () => props.unidades,
      set: (valor: IProdutoUnidade[]) => {
        emit('update:unidades', valor);
      },
    });

    const computedFatorUnidadePrincipal = computed({
      get: () => props.fatorUnidadePrincipal,
      set: (valor: number) => {
        emit('update:fatorUnidadePrincipal', valor);
      },
    });

    function preencherColunas() {
      state.colunas = [];
      state.colunas.push({
        title: 'Código', dataIndex: 'codigo', key: 'Codigo', position: 0, visible: false, ordering: true,
      });
      state.colunas.push({
        title: 'UN Principal', dataIndex: 'siglaUnidadePrincipal', key: 'SiglaUnidadePrincipal', width: 120, position: 1, visible: true, ordering: true, align: 'center',
      });
      state.colunas.push({
        title: 'UN Conversão', dataIndex: 'siglaUnidade', key: 'SiglaUnidade', width: 120, position: 2, visible: true, ordering: true, align: 'center',
      });
      state.colunas.push({
        title: 'Equivalência', dataIndex: 'descricaoEquivalencia', key: 'DescricaoEquivalencia', position: 3, visible: true, ordering: true, align: 'left',
      });
      state.colunas.push({
        title: '% Formação Preço', dataIndex: 'percentualFormacaoPreco', key: 'PercentualFormacaoPreco', width: 120, ellipsis: true, position: 3, visible: true, ordering: true, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado,
      });
      state.colunas.push({
        title: 'Ações', key: 'acoes', position: 4, visible: true, align: 'center', width: 70, customRenderRow: ECustomRenderRow.IconeAcoes,
      });
    }

    function verificarCampos() {
      if (!UtilitarioGeral.valorValido(state.unidadeSelecionada.codigoUnidade)) {
        apresentarMensagemAlerta('A de conversão é obrigatória!');
        return false;
      }

      if (state.unidadeSelecionada.equivalenciaPrincipal === 0) {
        apresentarMensagemAlerta('A quantidade principal deve ser informada!');
        return false;
      }

      if (state.unidadeSelecionada.equivalenciaSecundaria === 0) {
        apresentarMensagemAlerta('A quantidade de equivalência deve ser informada!');
        return false;
      }

      return true;
    }

    function calcularFatorConversao() {
      let fator = 0;
      // if (state.unidadeSelecionada.equivalenciaPrincipal > state.unidadeSelecionada.equivalenciaSecundaria) {
      //  fator = state.unidadeSelecionada.equivalenciaPrincipal;
      // } else {
      fator = state.unidadeSelecionada.equivalenciaPrincipal / state.unidadeSelecionada.equivalenciaSecundaria;
      // }
      return fator;
    }

    function adicionarUnidade() {
      const retorno = verificarCampos();
      if (!retorno) return;

      const unidadeProduto:IProdutoUnidade = {} as IProdutoUnidade;
      unidadeProduto.codigo = 0;
      unidadeProduto.codigoProdutoDefinicao = 0;
      unidadeProduto.codigoProdutoCodigo = 0;
      unidadeProduto.codigoProduto = state.unidadeSelecionada.codigoProduto;
      unidadeProduto.codigoUnidade = state.unidadeSelecionada.codigoUnidade;
      unidadeProduto.fator = state.unidadeSelecionada.equivalenciaSecundaria;
      unidadeProduto.siglaUnidade = state.unidadeSelecionada.siglaUnidade;
      unidadeProduto.siglaUnidadePrincipal = state.unidadeSelecionada.siglaUnidadePrincipal;
      unidadeProduto.fatorConversao = calcularFatorConversao();
      unidadeProduto.percentualFormacaoPreco = state.unidadeSelecionada.percentualFormacaoPreco;
      unidadeProduto.descricaoEquivalencia = `${state.unidadeSelecionada.equivalenciaPrincipal} ${state.unidadeSelecionada.siglaUnidadePrincipal} equivale a ${state.unidadeSelecionada.equivalenciaSecundaria} ${state.unidadeSelecionada.siglaUnidade}`;
      computedUnidades.value.push(unidadeProduto);
      state.unidadeSelecionada = {} as IProdutoUnidade;
      state.unidadeSelecionada.percentualFormacaoPreco = 0;
    }

    function removerUnidade(index: number) {
      computedUnidades.value.splice(index, 1);
    }

    function limparTela() {
      state.unidadeSelecionada = {} as IProdutoUnidade;
      state.unidadeSelecionada.percentualFormacaoPreco = 0;
      state.unidadeSelecionada.codigo = 0;
      state.unidadeSelecionada.codigoProdutoDefinicao = 0;
      state.unidadeSelecionada.codigoProdutoCodigo = 0;
      state.unidadeSelecionada.codigoProduto = '0';
      state.unidadeSelecionada.codigoUnidade = 0;
      state.unidadeSelecionada.siglaUnidade = '';
      state.unidadeSelecionada.siglaUnidadePrincipal = '';
      state.unidadeSelecionada.fator = 0;
      state.unidadeSelecionada.fatorConversao = 0;
      state.unidadeSelecionada.equivalenciaPrincipal = 0;
      state.unidadeSelecionada.equivalenciaSecundaria = 0;
      state.unidadeSelecionada.descricaoEquivalencia = '';
      state.unidadeSelecionada.percentualFormacaoPreco = 0;
    }

    onBeforeMount(async () => {
      preencherColunas();
      limparTela();
    });

    return {
      props,
      state,
      telaBase,
      ECustomRenderRow,
      computedFatorUnidadePrincipal,
      computedUnidades,
      UtilitarioMascara,
      UtilitarioGeral,
      adicionarUnidade,
      removerUnidade,
    };
  },
});
